import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import Avatar from '@/components/Avatar';
import Wordmark from '@/components/Wordmark';
import { useAuth } from '@/hooks/auth';
import { useAccountModal } from '@/hooks/dialogs';
import { useGlobalMenuSheet, usePlayerlistSheet } from '@/hooks/sheets';
import { useFcAdminAccess, useFcAdminSummary } from '@/hooks/fcAdmin';
import { useGlobalStatus } from '@/hooks/status';
import { playerCountAtom } from '@/hooks/playerlist';
import { useAtomValue } from 'jotai';
import { KeyRoundIcon, LogOutIcon, MenuIcon, UsersIcon } from 'lucide-react';
import { msToShortDuration } from '@/lib/dateTime';
import { cn } from '@/lib/utils';

function StatusChip({ label, value, tone = 'accent' }: { label: string; value: string; tone?: 'accent' | 'good' | 'warn' | 'danger' }) {
    return (
        <div className="fc-pulse-chip">
            <span className={cn('fc-pulse-dot', tone)} />
            <span className="fc-pulse-copy"><small>{label}</small><strong>{value}</strong></span>
        </div>
    );
}

function AccountMenu() {
    const { authData, logout } = useAuth();
    const { setAccountModalOpen } = useAccountModal();
    const { status, authorized } = useFcAdminAccess();
    if (!authData) return null;
    return (
        <DropdownMenu>
            <DropdownMenuTrigger className="fc-actor-chip">
                <span className="fc-actor-copy">
                    <small>{authorized ? status?.actor?.role ?? 'FC ADMIN' : 'FC ADMIN'}</small>
                    <strong>{authorized ? status?.actor?.name ?? authData.name : authData.name}</strong>
                </span>
                <Avatar username={authData.name} profilePicture={authData.profilePicture} className="fc-actor-avatar" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-56">
                <DropdownMenuItem className="cursor-pointer" onClick={() => setAccountModalOpen(true)}>
                    <KeyRoundIcon />Mein txAdmin-Konto
                </DropdownMenuItem>
                {window.txConsts.isWebInterface && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="cursor-pointer text-fc-red-soft focus:bg-fc-red/[0.12] focus:text-fc-red-bright" onClick={() => void logout()}>
                            <LogOutIcon />Abmelden
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export function Header() {
    const { setIsSheetOpen: openMenu } = useGlobalMenuSheet();
    const { setIsSheetOpen: openPlayers } = usePlayerlistSheet();
    const globalStatus = useGlobalStatus();
    const playerCount = useAtomValue(playerCountAtom);
    const { summary } = useFcAdminSummary();
    const { mutationReady } = useFcAdminAccess();
    const online = globalStatus?.server.health === 'ONLINE';
    const uptime = globalStatus?.server.uptime ? msToShortDuration(globalStatus.server.uptime, { units: ['d', 'h', 'm'] }) : '–';
    const fcReady = mutationReady;

    return (
        <header className="fc-topbar">
            <button className="fc-mobile-button menu" type="button" aria-label="Navigation öffnen" onClick={() => openMenu(true)}>
                <MenuIcon />
            </button>
            <div className="fc-brand-lockup">
                <Wordmark />
                <div className="fc-brand-copy"><strong>FC ADMIN</strong></div>
            </div>
            <div className="fc-topbar-status">
                <StatusChip label="SPIELER" value={String(playerCount)} tone="accent" />
                <StatusChip label="TEAM" value={summary?.team === undefined ? '–' : String(summary.team)} tone="accent" />
                <StatusChip label="SERVER" value={online ? 'ONLINE' : 'OFFLINE'} tone={online ? 'good' : 'danger'} />
                <StatusChip label="UPTIME" value={uptime} tone="warn" />
                <StatusChip label="FC-SERVICE" value={fcReady ? 'BEREIT' : 'EINGESCHRÄNKT'} tone={fcReady ? 'good' : 'warn'} />
            </div>
            <div className="fc-topbar-spacer" />
            <button className="fc-mobile-button players" type="button" aria-label="Spielerliste öffnen" onClick={() => openPlayers(true)}>
                <UsersIcon />
                <span>{playerCount}</span>
            </button>
            <AccountMenu />
        </header>
    );
}
